import { z } from "zod";
import { BusinessDate, EntityBase, Instant } from "./common.js";
import { Id } from "./ids.js";
import { Rial } from "./money.js";
import { MobilePhone } from "./phone.js";

/**
 * Food party module contracts (§12, architecture §16, §17).
 * High-quality surplus food is sold to the public at a discount so it does not go to waste.
 */

export const SurplusSource = z.enum([
  "production_overrun", // Made more than ordered (§12.2)
  "late_cancellation", // Late cancellation after cooking
  "undelivered_takeout", // Takeout not picked up / delivered
  "manual", // Direct manager surplus registration
]);
export type SurplusSource = z.infer<typeof SurplusSource>;

export const DiscountType = z.enum(["percent", "fixed_amount"]);
export type DiscountType = z.infer<typeof DiscountType>;

export const FoodpartyListingStatus = z.enum([
  "active", // Available for reservation
  "depleted", // All portions reserved/sold
  "expired", // Closed due to safety window or pickup deadline passed
  "closed", // Closed manually by manager
]);
export type FoodpartyListingStatus = z.infer<typeof FoodpartyListingStatus>;

export const FoodpartyReservationStatus = z.enum([
  "reserved", // Reserved, awaiting payment and pickup
  "delivered", // Handed over with delivery code and paid
  "cancelled", // Cancelled by customer or staff
  "released", // Unclaimed after pickup deadline, released back to surplus or expired
]);
export type FoodpartyReservationStatus = z.infer<typeof FoodpartyReservationStatus>;

export const FoodpartyPaymentStatus = z.enum([
  "pending", // Not paid yet
  "paid_counter", // Paid at the counter
  "paid_online", // Paid online (seam)
]);
export type FoodpartyPaymentStatus = z.infer<typeof FoodpartyPaymentStatus>;

export const FoodpartyPaymentMethod = z.enum([
  "counter", // In person at counter (default)
  "online", // Online payment interface seam
]);
export type FoodpartyPaymentMethod = z.infer<typeof FoodpartyPaymentMethod>;

/** 6-digit handover delivery code (§12.4, §12.7). */
export const DeliveryCode = z.string().regex(/^\d{6}$/);

/**
 * Surplus listing entity (§12.3).
 */
export const FoodpartyListing = EntityBase.extend({
  foodId: Id,
  foodName: z.string().trim().min(1).max(120),
  source: SurplusSource,
  sourceRefId: z.string().nullable().optional(),
  portionsTotal: z.int().positive(),
  portionsAvailable: z.int().nonnegative(),
  portionsReserved: z.int().nonnegative(),
  portionsSold: z.int().nonnegative(),
  originalPriceRial: Rial,
  discountType: DiscountType,
  discountValue: z.int().positive(),
  finalPriceRial: Rial,
  cookedAt: Instant,
  safetyExpiresAt: Instant,
  pickupDeadline: Instant,
  pickupPlace: z.string().trim().min(1).max(120),
  status: FoodpartyListingStatus,
  notes: z.string().trim().max(500).nullable().optional(),
});
export type FoodpartyListing = z.infer<typeof FoodpartyListing>;

/**
 * Food party reservation entity (§12.4).
 */
export const FoodpartyReservation = EntityBase.extend({
  listingId: Id,
  customerPhone: MobilePhone,
  customerName: z.string().trim().max(80).nullable().optional(),
  portions: z.int().positive(),
  unitPriceRial: Rial,
  totalPriceRial: Rial,
  deliveryCode: DeliveryCode,
  status: FoodpartyReservationStatus,
  paymentStatus: FoodpartyPaymentStatus,
  paymentMethod: FoodpartyPaymentMethod,
  reservedAt: Instant,
  pickupDeadline: Instant,
  deliveredAt: Instant.nullable().optional(),
  cancelledAt: Instant.nullable().optional(),
  cancellationReason: z.string().trim().max(300).nullable().optional(),
});
export type FoodpartyReservation = z.infer<typeof FoodpartyReservation>;

/**
 * Opt-in public customer record (§12.4).
 */
export const FoodpartyCustomer = EntityBase.extend({
  phone: MobilePhone,
  name: z.string().trim().max(80).nullable().optional(),
  optedInSms: z.boolean(),
  noShowCount: z.int().nonnegative(),
  lastReservationAt: Instant.nullable().optional(),
});
export type FoodpartyCustomer = z.infer<typeof FoodpartyCustomer>;

/**
 * Suggested surplus item computed from production / orders exports (§12.2).
 */
export const SurplusSuggestion = z.object({
  id: z.string(),
  foodId: Id,
  foodName: z.string().trim().min(1).max(120),
  source: SurplusSource,
  sourceRefId: z.string(),
  portions: z.int().positive(),
  cookedAt: Instant,
  originalPriceRial: Rial,
  suggestedDiscountPercent: z.int().min(1).max(99),
  details: z.string(),
});
export type SurplusSuggestion = z.infer<typeof SurplusSuggestion>;

/** Input for creating a new surplus listing by manager (§12.2, §12.3). */
export const CreateFoodpartyListingInput = z.object({
  foodId: Id,
  foodName: z.string().trim().min(1).max(120),
  source: SurplusSource.default("manual"),
  sourceRefId: z.string().nullable().optional(),
  portions: z.int().positive(),
  originalPriceRial: Rial,
  discountType: DiscountType.default("percent"),
  discountValue: z.int().positive(),
  cookedAt: Instant,
  safetyHours: z.number().positive().max(12).optional(), // Defaults to manager setting (4h)
  pickupMinutes: z.number().positive().max(720).optional(), // Defaults to safety expiry
  pickupPlace: z.string().trim().min(1).max(120).optional(),
  notes: z.string().trim().max(500).nullable().optional(),
});
export type CreateFoodpartyListingInput = z.input<typeof CreateFoodpartyListingInput>;

/** Input for placing a reservation (§12.4). */
export const CreateFoodpartyReservationInput = z.object({
  listingId: Id,
  customerPhone: MobilePhone,
  customerName: z.string().trim().max(80).nullable().optional(),
  portions: z.int().positive(),
  paymentMethod: FoodpartyPaymentMethod.default("counter"),
});
export type CreateFoodpartyReservationInput = z.input<typeof CreateFoodpartyReservationInput>;

/** Input for delivering a reservation at the counter (§12.4, §12.7). */
export const DeliverReservationInput = z.object({
  deliveryCode: DeliveryCode,
  paidAmountRial: Rial.optional(),
});
export type DeliverReservationInput = z.infer<typeof DeliverReservationInput>;

/**
 * Public isolated listing view (§12.6).
 * Strictly contains NO internal information (no kitchen, no staff, no organization, no cost).
 */
export const PublicListingView = z.object({
  id: Id,
  foodName: z.string(),
  portionsAvailable: z.int().nonnegative(),
  discountPercent: z.int().min(1).max(99),
  finalPriceRial: Rial,
  pickupDeadline: Instant,
  pickupPlace: z.string(),
  status: FoodpartyListingStatus,
});
export type PublicListingView = z.infer<typeof PublicListingView>;

/**
 * Public reservation view for the customer's own reservations (§12.6).
 */
export const PublicReservationView = z.object({
  id: Id,
  listingId: Id,
  foodName: z.string(),
  portions: z.int().positive(),
  unitPriceRial: Rial,
  totalPriceRial: Rial,
  deliveryCode: DeliveryCode,
  status: FoodpartyReservationStatus,
  paymentStatus: FoodpartyPaymentStatus,
  pickupDeadline: Instant,
  pickupPlace: z.string(),
  reservedAt: Instant,
});
export type PublicReservationView = z.infer<typeof PublicReservationView>;

/**
 * Report «غذای نجات‌یافته» (§12.7).
 */
export const SavedFoodReportRow = z.object({
  date: BusinessDate,
  foodName: z.string(),
  portionsSaved: z.int().nonnegative(),
  savedRevenueRial: Rial,
  originalValueRial: Rial,
  discountGivenRial: Rial,
});
export type SavedFoodReportRow = z.infer<typeof SavedFoodReportRow>;

export const SavedFoodReport = z.object({
  periodStart: BusinessDate,
  periodEnd: BusinessDate,
  totalPortionsSaved: z.int().nonnegative(),
  totalSavedRevenueRial: Rial,
  totalOriginalValueRial: Rial,
  totalDiscountGivenRial: Rial,
  rows: z.array(SavedFoodReportRow),
});
export type SavedFoodReport = z.infer<typeof SavedFoodReport>;

/**
 * Pure calculation: Computes discounted price in integer rial.
 */
export function calculateFinalPrice(
  originalPriceRial: Rial,
  discountType: DiscountType,
  discountValue: number,
): Rial {
  let finalPrice: number;
  if (discountType === "percent") {
    const pct = Math.max(1, Math.min(99, discountValue));
    finalPrice = Math.round((Number(originalPriceRial) * (100 - pct)) / 100);
  } else {
    finalPrice = Number(originalPriceRial) - discountValue;
  }
  finalPrice = Math.max(0, Math.min(Number(originalPriceRial), finalPrice));
  return Rial.parse(finalPrice);
}

/**
 * Pure calculation: Computes effective discount percent (1-99).
 */
export function calculateDiscountPercent(originalPriceRial: Rial, finalPriceRial: Rial): number {
  const orig = Number(originalPriceRial);
  const fin = Number(finalPriceRial);
  if (orig <= 0) return 0;
  const pct = Math.round(((orig - fin) / orig) * 100);
  return Math.max(0, Math.min(100, pct));
}

/**
 * Generates a random 6-digit delivery code.
 */
export function generateDeliveryCode(): string {
  const code = Math.floor(100000 + Math.random() * 900000);
  return String(code);
}
